import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import SelectContactsList from '../../../components/Contact/SelectContactsList/SelectContactsList';
import ButtonLoading from '../../../components/Shared/ButtonLoading';
import useAsyncGetListContactsLite from '../../../hooks/useAsyncGetListContactsLite';
import { DEFAULT_COMMON_FORM_NUMBER } from '../../../sic-lib/defaults';
import { RequestStatus } from '../../../sic-lib/enums/RequestStatus';
import { GenericReducer, genericReducer } from '../../../sic-lib/reducers/genericReducer';
import { buildGetListContacts } from '../../../sic-lib/types/ListContact';
import { IErrorFormValidation } from '../../../sic-lib/utils/formValidationHelpers';
import { mapContactToApSchedulerContact } from '../../../sic-lib/utils/functionalMappers';
import { setContacts } from '../../../store/communicator';
import { useAppDispatch } from '../../../store/hooks';

const useStyles = makeStyles()((theme: Theme) => ({
    root: {
        height: '100%',
        maxWidth: '400px',
        margin: '0 auto',
        [theme.breakpoints.up(0)]: {
            padding: theme.spacing(7, 1, 0),
        },
        [theme.breakpoints.up('sm')]: {
            padding: theme.spacing(7, 2, 0),
        },
    },
    textWrapper: {
        margin: theme.spacing(3.5, 0),
    },
    selectWrapper: {
        margin: theme.spacing(3, 0),
    },
    buttonWrapper: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
    },
}));

interface ComponentState {
    listId: number;
    formError: IErrorFormValidation | null;
}

export default function CommContactsSelect() {
    const dispatch = useAppDispatch();
    const { classes } = useStyles();

    const { asyncGetListContacts, requestStatus } = useAsyncGetListContactsLite();

    const [compState, compDispatch] = React.useReducer<GenericReducer<ComponentState>>(
        genericReducer,
        {
            listId: DEFAULT_COMMON_FORM_NUMBER,
            formError: null,
        },
    );

    const handleSubmit = async () => {
        try {
            const reqObj = buildGetListContacts({
                validateListId: true,
                listId: compState.listId,
            });

            const asyncResult = await asyncGetListContacts(reqObj);

            if (asyncResult) {
                const contacts = asyncResult.map(mapContactToApSchedulerContact);

                dispatch(setContacts(contacts));
            }
        } catch (error: any) {
            if (error.isFormError) {
                compDispatch({ formError: error as IErrorFormValidation });
            }
        }
    };

    return (
        <div className={classes.root}>
            <div className={classes.textWrapper}>
                <Typography component="p" variant="body1" align="center">
                    Select a contact list to be used as the target of the campaign.
                </Typography>
            </div>
            <div className={classes.selectWrapper}>
                <SelectContactsList
                    fullWidth
                    variant="outlined"
                    error={Boolean(compState.formError?.field === 'listId')}
                    helperText={
                        compState.formError?.field === 'listId' ? compState.formError.message : ''
                    }
                    value={compState.listId || ''}
                    onSelectHandler={compDispatch}
                />
            </div>
            <div className={classes.buttonWrapper}>
                <ButtonLoading
                    isLoading={requestStatus === RequestStatus.active}
                    onClick={handleSubmit}
                    color="secondary"
                    variant="outlined"
                >
                    Select Contacts
                </ButtonLoading>
            </div>
        </div>
    );
}
